/*
 * Mock-spezifischer Cursor-Context-Builder.
 *
 * Delegiert an den echten `buildCursorContext` (cursorContext.js), damit Mock
 * und Real dieselbe Shape liefern (pane mit Background-Detection,
 * paragraphIndex, IMPLICIT_HEAD_ID-Token, selection-Block). Mock-spezifisch
 * ergaenzen wir nur Felder, die der Mock-Apply-Pfad braucht und die der
 * echte Server-Pfad serverseitig aus der DB zieht:
 *   - text         — Plain-Text des Cursor-Paragraphen (fuer Scenarios mit
 *                    `$cursor.text`)
 *   - version      — Paragraph-Version aus dem ProjectManager-Cache (gegen den
 *                    der ReviewController beim Accept prueft)
 *   - textParagraphs — flache Liste aller Text-Paragraphen (Multi-Vorschlag-
 *                    Scenarios greifen direkt darauf zu)
 *
 * Pre-F4: Der Mock baute eine eigene, divergente Cursor-Context-Shape
 * (`pane: 'main'` immer, kein paragraphIndex, kein IMPLICIT_HEAD_ID-Token).
 * Heute kein Bug, aber latente Drift fuer kuenftige Scenarios. Siehe
 * docs/archive/review-2026-05-03.md F4.
 */

import { buildCursorContext } from './cursorContext.js'

/**
 * @param {object} ctx - appContext (mit editor, layout, projectManager)
 * @returns {object|null}
 */
export function buildCursorContextForMock(ctx) {
    if (!ctx?.editor?.model || !ctx?.layout) return null
    const real = buildCursorContext({
        model: ctx.editor.model,
        layout: ctx.layout
    })
    if (!real) return null

    const model = ctx.editor.model
    const cursor = model.cursor
    const para = cursor?.paragraphId ? model.getParagraph(cursor.paragraphId) : null

    // Version-Lookup MUSS aus dem ProjectManager kommen — derselbe Cache, gegen
    // den ReviewController._versionMismatch beim Accept prueft. para.version am
    // Modell ist faktisch nie gesetzt; ein Default `: 1` triggert nach dem
    // ersten Cloud-Sync (Phase 5) systematisch version_mismatch und macht
    // jeden Mock-Accept zum Skip. Undefined schickt der Mock weiter als
    // `baseVersion: undefined` → kein Versions-Check.
    const versionOf = (paragraphId) => {
        const v = ctx.projectManager?.getParagraphVersion?.(paragraphId)
        return typeof v === 'number' ? v : undefined
    }
    const textParagraphs = []
    for (const p of model.paragraphs) {
        if (p.type === 'separator') continue
        textParagraphs.push({
            id: p.id,
            text: p.text || '',
            version: versionOf(p.id)
        })
    }

    return {
        ...real,
        text: para?.text || '',
        version: versionOf(cursor?.paragraphId),
        textParagraphs
    }
}
